import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, UserPlus } from "lucide-react";
import { type FormEvent, useState } from "react";

import { BackLink } from "../components/BackLink";

import { AccountPicker } from "../components/AccountPicker";
import { ApiError, accountsApi, contactsApi, isDuplicatePhoneDetail } from "../lib/api";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/contacts/new")({
  component: NewContactPage,
});

interface DuplicateState {
  message: string;
  phone: string;
}

function NewContactPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [accountId, setAccountId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dup, setDup] = useState<DuplicateState | null>(null);

  // Pre-fetch accounts so the picker is ready as soon as the user opens
  // it. We share the cache key with `<AccountPicker>` so it doesn't refetch.
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setDup(null);
    setSubmitting(true);
    try {
      const created = await contactsApi.create({
        name,
        phone,
        email: email.trim() || null,
        address: address.trim() || null,
        account_id: accountId,
      });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success("Contact created.");
      navigate({ to: "/contacts/$contactId", params: { contactId: created.id } });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && isDuplicatePhoneDetail(err.detail)) {
        // PRD §10.1: route to the existing contact, or show "ask your manager".
        if (err.detail.code === "duplicate_phone_visible") {
          queryClient.invalidateQueries({ queryKey: ["contacts"] });
          toast.info("This phone number is already on file — opened the existing contact.");
          navigate({
            to: "/contacts/$contactId",
            params: { contactId: err.detail.existing_contact_id },
          });
          return;
        }
        setDup({ message: err.detail.message, phone: err.detail.phone });
      } else {
        const msg =
          err instanceof ApiError
            ? err.message
            : "Could not reach the server. Check your connection.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/contacts" fallbackLabel="contacts" />
        <h1 className="text-2xl font-semibold tracking-tight">New contact</h1>
        <p className="text-sm opacity-60">
          Phone number is the unique key — we'll catch duplicates automatically.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-xl border border-[var(--border)] p-5"
        noValidate
      >
        <Field
          id="contact-phone"
          label="Phone"
          hint="Indian numbers can be entered as 10 digits; otherwise prefix with +<country>."
        >
          <input
            id="contact-phone"
            type="tel"
            required
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9876543210"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field id="contact-name" label="Name">
          <input
            id="contact-name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Asha Rao"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field id="contact-email" label="Email" hint="Optional.">
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="asha@buyer.example"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field
          id="contact-address"
          label="Address"
          hint="Optional. Used as the default delivery address."
        >
          <textarea
            id="contact-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Shipping / delivery address"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field
          id="contact-account"
          label="Account"
          hint="Optional, but a contact must belong to an account before its deal can be marked Won/Lost."
        >
          <AccountPicker
            id="contact-account"
            value={accountId}
            onChange={setAccountId}
            accounts={accountsQ.data?.items ?? []}
            loading={accountsQ.isLoading}
            allowCreate
          />
        </Field>

        {dup && (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {dup.message}
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !phone.trim() || !name.trim()}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            <>
              <UserPlus className="size-4" aria-hidden />
              Create contact
            </>
          )}
        </button>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80">
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}
